<template>
    <component
        :is="'li'"
        class="AssociatedAccountListItem"
    >
        <div class="h3">
            {{ associatedAccount.getFullName() }}
        </div>
        <div
            v-for="provider in associatedAccount.providers"
            :key="provider.id"
        >
            <div v-if="!isProviderHasDevices(provider.accounts)">
                <template
                    v-for="providerAccount in provider.accounts"
                    :key="providerAccount.providerUserExternalId"
                >
                    <fare-profile
                        v-if="!isEmpty(provider.walletManageFareProfile)"
                        :provider="provider"
                        :providerAccount="providerAccount"
                        class="AssociatedAccountListItem-fareProfile"
                    />
                    <template v-if="providerAccount?.fareMedias?.length > 0">
                        <section
                            v-if="providerAccount.hasPhysicalFareMedias()"
                            class="AssociatedAccountListItem-list AssociatedAccountListItem-fareMediaList"
                        >
                            <div class="AssociatedAccountListItem-subtitle mb-4">
                                <icomoon-icon
                                    name="Card"
                                    class="AssociatedAccountListItem-subtitleIcon"
                                />
                                <h3 class="AssociatedAccountListItem-subtitleText">
                                    {{ $t('account:tickets:tabs:cards') }}
                                </h3>
                            </div>

                            <div
                                v-for="fareMedia in providerAccount.getPhysicalFareMedias()"
                                :key="fareMedia.id"
                            >
                                <fare-media-item
                                    :fareMedia="fareMedia"
                                    class="AssociatedAccountListItem-fareMediaItem"
                                    @download-fare-media-certificate-button-clicked="downloadFareMediaCertificateButtonClicked"
                                    @suspend-subscription-button-clicked="suspendSubscriptionButtonClicked"
                                    @update-fare-media="updateFareMedia"
                                />
                            </div>
                        </section>
                        <section
                            v-if="providerAccount.hasDematerializedFareMedias()"
                            class="AssociatedAccountListItem-list AssociatedAccountListItem-fareMediaList"
                        >
                            <div class="AssociatedAccountListItem-subtitle mb-4">
                                <icomoon-icon
                                    name="Phone"
                                    class="AssociatedAccountListItem-subtitleIcon"
                                />
                                <h3 class="AssociatedAccountListItem-subtitleText">
                                    {{ $t('account:tickets:dematerialized_cards') }}
                                </h3>
                            </div>

                            <div
                                v-for="fareMedia in providerAccount.getDematerializedFareMedias()"
                                :key="fareMedia.id"
                            >
                                <fare-media-item
                                    :fareMedia="fareMedia"
                                    class="AssociatedAccountListItem-fareMediaItem"
                                    @download-fare-media-certificate-button-clicked="downloadFareMediaCertificateButtonClicked"
                                    @suspend-subscription-button-clicked="suspendSubscriptionButtonClicked"
                                    @update-fare-media="updateFareMedia"
                                />
                            </div>
                        </section>
                    </template>
                    <div v-else>
                        <empty-cards />
                    </div>
                </template>
            </div>
        </div>
        <fare-media-add-modal
            v-model="showFareMediaAddModal"
            :is-coming-from-fare-media-choice-modal="false"
            @go-next-action="fareMediaAddModalCloseAction"
            @close-modal="closeAllFareMediaChoiceOrAddModals"
        />
        <fare-media-add-new-modal
            v-model="showFareMediaAddNewModal"
            :is-coming-from-fare-media-choice-modal="false"
            @go-next-action="fareMediaAddNewModalCloseAction"
            @close-modal="closeAllFareMediaChoiceOrAddModals"
        />
        <fare-media-add-existant-modal
            v-model="showFareMediaAddExistantModal"
            :is-coming-from-fare-media-choice-modal="false"
            @go-next-action="fareMediaAddExistantModalCloseAction"
            @close-modal="closeAllFareMediaChoiceOrAddModals"
        />
    </component>
</template>

<script setup>
import IcomoonIcon from '@/StoreWeb/components/common/IcomoonIcon.vue'
import FareMediaAddExistantModal from '@/StoreWeb/components/fare-media/FareMediaAddExistantModal'
import FareMediaAddModal from '@/StoreWeb/components/fare-media/FareMediaAddModal'
import FareMediaAddNewModal from '@/StoreWeb/components/fare-media/FareMediaAddNewModal'
import FareMediaItem from '@/StoreWeb/components/fare-media/FareMediaItem.vue'
import FareProfile from '@/StoreWeb/components/fare-profile/FareProfile.vue'
import { useModalActions } from '@/StoreWeb/js/composables/add-faremedia-modals-utils'
import { isEmpty } from 'global-utils'

const {
    closeAllFareMediaChoiceOrAddModals,
    fareMediaAddExistantModalCloseAction,
    fareMediaAddModalCloseAction,
    fareMediaAddNewModalCloseAction,
    showFareMediaAddExistantModal,
    showFareMediaAddModal,
    showFareMediaAddNewModal
} = useModalActions()

const emit = defineEmits(['hideFareMediaChoiceModal', 'downloadFareMediaCertificateButtonClicked', 'suspendSubscriptionButtonClicked', 'updateFareMedia'])

defineProps({
    associatedAccount: {
        type: Object,
        required: true
    },
    displayedListType: {
        type: String,
        default: 'ALL',
        validator: val => ['ALL', 'COUNTERS', 'FAREMEDIAS', 'MTICKETS', 'PLANS', 'SUPPORTS'].includes(val)
    }
})

function isProviderHasDevices (accounts) {
    return accounts.some(account => account.devices.length > 0)
}

function downloadFareMediaCertificateButtonClicked (data) {
    emit('downloadFareMediaCertificateButtonClicked', data)
}

function suspendSubscriptionButtonClicked (data) {
    emit('suspendSubscriptionButtonClicked', data)
}

function updateFareMedia (data) {
    emit('updateFareMedia', data)
}
</script>

<style lang="scss" scoped>
@import 'globalScss';

.AssociatedAccountListItem {
    margin-bottom: 30px;
    list-style: none;

    &:last-child {
        margin-bottom: 0;
    }

    &-subtitle {
        display: flex;
        align-items: center;
        gap: $s2;
    }

    &-subtitleText {
        @extend %h2;

        margin: 0;
        font-size: $font-smedium;

        @media #{map-get($display-breakpoints, 'sm-and-up')} {
            font-size: $font-large;
        }
    }

    &-subtitleIcon {
        font-size: 32px;
        color: $primary_color;
    }

    &-list {
        margin: 0 0 25px;
        padding: 0;
        list-style: none;

        &:last-child {
            margin-bottom: 0;
        }
    }
}
</style>
